import { defineStore } from 'pinia'

// types
import type {
  Task,
  TaskApiResponse,
  CreateTaskApiPayload,
  UpdateTaskPayload,
  UpdateTaskApiPayload
} from '@/types/domain/tasks'
import type { UserReference } from '@/types/domain/user'

// composables
import useApiRequest from '@/composables/useApiRequest'

// utilities
import { alertToast } from '@/utilities/notification'

// stores
import { useAuthStore } from './AuthStore'

/**
 * Normalizes a UserReference from API response (can be number or UserReference)
 * If it's a number, creates a minimal UserReference placeholder
 * @param user - User ID (number) or UserReference object
 * @returns UserReference object
 */
function normalizeUserReference(user: number | UserReference): UserReference {
  if (typeof user === 'number') {
    // Create a minimal UserReference placeholder when API returns just an ID
    // This can be enhanced later to fetch full user details if needed
    return {
      id: user,
      forename: '',
      surname: '',
      initials: '',
      profilePicUrl: null
    }
  }
  return user
}

/**
 * Transforms API response to internal Task format
 * @param apiTask - Task from API response
 * @returns Normalized Task for internal use
 */
function normalizeTaskApiResponse(apiTask: TaskApiResponse): Task {
  return {
    id: apiTask.id,
    assignee: normalizeUserReference(apiTask.assignee),
    title: apiTask.title,
    description: apiTask.description,
    tagsString: apiTask.tagsString,
    tags: apiTask.tags,
    dueDate: apiTask.dueDate,
    linkType: apiTask.linkType,
    linkTitle: apiTask.linkTitle,
    linkId: apiTask.linkId ?? 0,
    isReminder: apiTask.isReminder,
    isStarred: apiTask.isStarred,
    isCompleted: apiTask.isCompleted,
    isDeleted: apiTask.isDeleted,
    createdBy: normalizeUserReference(apiTask.createdBy),
    dateCreated: apiTask.dateCreated ?? apiTask.createdAt ?? null
  }
}

/**
 * Ensures CreateTaskApiPayload has required fields with defaults
 * @param payload - Create task API payload
 * @param currentUserId - Current user ID (for assignee default)
 * @returns API payload for POST request with defaults applied
 */
function ensureCreateTaskApiPayload(payload: CreateTaskApiPayload, currentUserId: number): CreateTaskApiPayload {
  return {
    assignee: payload.assignee ?? currentUserId,
    title: payload.title,
    description: payload.description ?? null,
    tags: payload.tags ?? null,
    dueDate: payload.dueDate ?? null,
    linkType: payload.linkType ?? null,
    linkTitle: payload.linkTitle ?? null,
    linkId: payload.linkId ?? null,
    isReminder: payload.isReminder ?? false,
    isStarred: payload.isStarred ?? false,
    isCompleted: payload.isCompleted ?? false,
    isDeleted: payload.isDeleted ?? false
  }
}

/**
 * Transforms internal UpdateTaskPayload to API payload format
 * @param payload - Internal update task payload
 * @param currentAssigneeId - Current assignee ID (required for API)
 * @returns API payload for PATCH request
 */
function updateTaskPayloadToApi(payload: Partial<UpdateTaskPayload>, currentAssigneeId: number): UpdateTaskApiPayload {
  const apiPayload: UpdateTaskApiPayload = {
    assignee: payload.assignee !== undefined ? payload.assignee : currentAssigneeId
  }

  if (payload.title !== undefined) apiPayload.title = payload.title
  if (payload.description !== undefined) apiPayload.description = payload.description
  if (payload.tags !== undefined) apiPayload.tags = payload.tags ?? null
  if (payload.dueDate !== undefined) apiPayload.dueDate = payload.dueDate ?? null
  if (payload.linkType !== undefined) apiPayload.linkType = payload.linkType ?? null
  if (payload.linkTitle !== undefined) apiPayload.linkTitle = payload.linkTitle ?? null
  if (payload.linkId !== undefined) apiPayload.linkId = payload.linkId ?? null
  if (payload.isReminder !== undefined) apiPayload.isReminder = payload.isReminder
  if (payload.isStarred !== undefined) apiPayload.isStarred = payload.isStarred
  if (payload.isDeleted !== undefined) apiPayload.isDeleted = payload.isDeleted
  if (payload.isCompleted !== undefined) apiPayload.isCompleted = payload.isCompleted

  return apiPayload
}

export const useTaskStore = defineStore('taskStore', {
  state: (): {
    tasks: Task[]
    isLoading: boolean
    outstandingTasks: number | null
  } => ({
    tasks: [],
    isLoading: false,
    outstandingTasks: null
  }),

  getters: {
    /**
     * Gets all tasks
     */
    allTasks(): Task[] {
      return this.tasks
    },

    /**
     * Calculates outstanding tasks count
     */
    calculatedOutstandingTasks(): number {
      const now = new Date()
      return this.tasks.filter(task => {
        if (task.isDeleted || task.isCompleted) return false

        const dueDate = task.dueDate && task.dueDate !== '0000-00-00 00:00:00'
        if (!dueDate || !task.dueDate || isNaN(new Date(task.dueDate).getTime())) return true

        return new Date(task.dueDate) >= now
      }).length
    }
  },

  actions: {
    /**
     * Fetches all tasks for the current user
     */
    async fetchTasks(): Promise<void> {
      const authStore = useAuthStore()

      if (!authStore.isLoggedIn || !authStore.details.id) {
        this.tasks = []
        this.outstandingTasks = null
        return
      }

      this.isLoading = true

      try {
        const apiRequest = useApiRequest()
        const response = await apiRequest.send({
          endpoint: `/v1/tasks?userId=${authStore.details.id}`,
          method: 'GET'
        })

        if (response.success && Array.isArray(response.data)) {
          // Transform API responses to internal Task format
          this.tasks = response.data.map((apiTask: TaskApiResponse) => normalizeTaskApiResponse(apiTask))
          this.updateOutstandingCount()
        } else {
          this.tasks = []
          this.outstandingTasks = 0
        }
      } catch (error) {
        console.error('Unable to fetch tasks', error)
        const errorMessage = error instanceof Error ? error.message : 'Failed to fetch tasks'
        alertToast('Failed to get Tasks', errorMessage, 'error')
        this.tasks = []
        this.outstandingTasks = 0
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Creates a new task
     * @param payload - Task data to create
     * @returns Promise with created task or null if failed
     */
    async createTask(payload: CreateTaskApiPayload): Promise<Task | null> {
      const authStore = useAuthStore()

      if (!authStore.isLoggedIn || !authStore.details.id) {
        alertToast('Error', 'You must be logged in to create a task', 'error')
        return null
      }

      try {
        const apiRequest = useApiRequest()
        // Ensure payload has required fields with defaults
        const currentUserId = Number(authStore.details.id)
        const apiPayload = ensureCreateTaskApiPayload(payload, currentUserId)
        // Store the original linkId from payload in case API doesn't return it
        const originalLinkId = apiPayload.linkId ?? null
        const jsonData = JSON.stringify(apiPayload)
        const response = await apiRequest.send({
          endpoint: '/v1/tasks',
          method: 'POST',
          data: jsonData
        })

        // If API call was successful and we have response data, add the task to the store
        // Also handle cases where success might be false but we still got a valid response (some APIs return 201 with data)
        if (response.data) {
          // Handle both cases: response.data might be the task object directly, or it might be an array
          let taskData: TaskApiResponse | null = null

          if (Array.isArray(response.data)) {
            // If response.data is an array, take the first element
            taskData = response.data[0] as TaskApiResponse
          } else if (typeof response.data === 'object' && response.data !== null) {
            taskData = response.data as TaskApiResponse
          }

          // Validate that we have a task object with an id
          if (taskData && typeof taskData === 'object' && 'id' in taskData && taskData.id) {
            try {
              // If API response doesn't include linkId but we sent one, preserve it
              // Check for null/undefined specifically (not falsy, since 0 is a valid linkId)
              if ((taskData.linkId === null || taskData.linkId === undefined) && originalLinkId !== null) {
                taskData.linkId = originalLinkId
              }

              // Transform API response to internal Task format
              const normalizedTask = normalizeTaskApiResponse(taskData)

              // Check if task already exists in store (avoid duplicates)
              const existingIndex = this.tasks.findIndex(t => t.id === normalizedTask.id)
              if (existingIndex === -1) {
                this.tasks.push(normalizedTask)
                this.updateOutstandingCount()
              } else {
                // Update existing task if it already exists
                this.tasks[existingIndex] = normalizedTask
                this.updateOutstandingCount()
              }

              alertToast('Added', null, 'success')
              return normalizedTask
            } catch (normalizeError) {
              console.error('Error normalizing task response:', normalizeError, taskData)
              alertToast('Error', 'Failed to process created task', 'error')
              return null
            }
          } else {
            console.warn('API returned data but task data is invalid or missing id:', response.data)
            // Even if the response structure is unexpected, if the API call succeeded,
            // we should still try to fetch tasks to ensure the store is up to date
            if (response.success) {
              // Silently refresh tasks from API
              await this.fetchTasks()
              alertToast('Added', null, 'success')
              return null
            } else {
              alertToast('Error', 'Failed to create task: invalid response', 'error')
              return null
            }
          }
        } else {
          // If no data but API call was successful, refresh tasks from API
          if (response.success) {
            await this.fetchTasks()
            alertToast('Added', null, 'success')
            return null
          } else {
            console.error('API call failed or returned no data:', response)
            alertToast('Error', 'Failed to create task', 'error')
            return null
          }
        }
      } catch (error) {
        console.error('Error creating task', error)
        const errorMessage = error instanceof Error ? error.message : 'Failed to create task'
        alertToast('Error Creating Task', errorMessage, 'error')
        return null
      }
    },

    /**
     * Updates an existing task
     * @param taskId - ID of the task to update
     * @param payload - Task data to update
     * @returns Promise with updated task or null if failed
     */
    async updateTask(taskId: string | number, payload: Partial<UpdateTaskPayload>): Promise<Task | null> {
      const taskIndex = this.tasks.findIndex(t => t.id === taskId)
      if (taskIndex === -1) {
        console.error('Task not found:', taskId)
        alertToast('Error', 'Task not found', 'error')
        return null
      }

      // Store original task for rollback on error
      const originalTask = { ...this.tasks[taskIndex] }

      // Optimistically update the task locally
      const updatedTask = { ...this.tasks[taskIndex] }

      // Map payload fields to task fields
      if (payload.assignee !== undefined) {
        // Update assignee ID but preserve UserReference structure
        updatedTask.assignee = { ...updatedTask.assignee, id: Number(payload.assignee) }
      }
      if (payload.title !== undefined) updatedTask.title = payload.title
      if (payload.description !== undefined) updatedTask.description = payload.description
      if (payload.tags !== undefined) updatedTask.tags = payload.tags
      if (payload.dueDate !== undefined) updatedTask.dueDate = payload.dueDate
      if (payload.linkType !== undefined) updatedTask.linkType = payload.linkType
      if (payload.linkTitle !== undefined) updatedTask.linkTitle = payload.linkTitle
      if (payload.linkId !== undefined) updatedTask.linkId = payload.linkId ?? 0
      if (payload.isReminder !== undefined) updatedTask.isReminder = payload.isReminder
      if (payload.isStarred !== undefined) updatedTask.isStarred = payload.isStarred
      if (payload.isCompleted !== undefined) updatedTask.isCompleted = payload.isCompleted
      if (payload.isDeleted !== undefined) updatedTask.isDeleted = payload.isDeleted

      try {
        const apiRequest = useApiRequest()
        // Transform internal payload to API payload format
        const apiPayload = updateTaskPayloadToApi(payload, updatedTask.assignee.id)
        const jsonData = JSON.stringify(apiPayload)
        const response = await apiRequest.send({
          endpoint: `/v1/tasks/${taskId}`,
          method: 'PATCH',
          data: jsonData
        })

        if (response.success) {
          // API returns empty body or updated task
          // If response.data exists and has content, transform and use it
          if (response.data && typeof response.data === 'object' && Object.keys(response.data).length > 0) {
            // Transform API response to internal Task format
            const normalizedTask = normalizeTaskApiResponse(response.data as TaskApiResponse)
            this.tasks[taskIndex] = normalizedTask
          } else {
            // If no response data, keep our optimistically updated task
            this.tasks[taskIndex] = updatedTask
          }
          this.updateOutstandingCount()
          alertToast('Updated', null, 'success')
          return this.tasks[taskIndex]
        } else {
          // Rollback on failure
          this.tasks[taskIndex] = originalTask
          alertToast('Error', 'Failed to update task', 'error')
          return null
        }
      } catch (error) {
        // Rollback on error
        this.tasks[taskIndex] = originalTask
        console.error('Error updating task', error)
        const errorMessage = error instanceof Error ? error.message : 'Failed to update task'
        alertToast('Error Updating Task', errorMessage, 'error')
        return null
      }
    },

    /**
     * Toggles the completed status of a task
     * @param taskId - ID of the task to toggle
     * @param isCompleted - New completed status (optional, will toggle if not provided)
     * @returns Promise with updated task or null if failed
     */
    async toggleComplete(taskId: string | number, isCompleted?: boolean): Promise<Task | null> {
      const task = this.tasks.find(t => t.id === taskId)
      if (!task) {
        console.error('Task not found:', taskId)
        return null
      }

      const newStatus = isCompleted !== undefined ? isCompleted : !task.isCompleted

      return this.updateTask(taskId, { isCompleted: newStatus })
    },

    /**
     * Toggles the deleted status of a task
     * @param taskId - ID of the task to toggle
     * @param isDeleted - New deleted status (optional, will toggle if not provided)
     * @returns Promise with updated task or null if failed
     */
    async toggleDelete(taskId: string | number, isDeleted?: boolean): Promise<Task | null> {
      const task = this.tasks.find(t => t.id === taskId)
      if (!task) {
        console.error('Task not found:', taskId)
        return null
      }

      const newStatus = isDeleted !== undefined ? isDeleted : !task.isDeleted

      return this.updateTask(taskId, { isDeleted: newStatus })
    },

    /**
     * Updates a task in the local state (for optimistic updates)
     * @param task - Updated task object
     */
    updateTaskLocal(task: Task): void {
      const index = this.tasks.findIndex(t => t.id === task.id)
      if (index !== -1) {
        this.tasks[index] = task
        this.updateOutstandingCount()
      }
    },

    /**
     * Updates the outstanding tasks count based on current tasks
     */
    updateOutstandingCount(): void {
      this.outstandingTasks = this.calculatedOutstandingTasks
    },

    /**
     * Updates the task count manually (for backward compatibility)
     * @param count - The new task count
     */
    updateTasks(count: number): void {
      this.outstandingTasks = count
    }
  }
})
